/**
 * Each band's colour, taken from the platform's own brand so the chart reads as
 * the platforms it counts. Three cases a raw brand hex cannot handle on its own:
 *
 * - Near-black and near-white brands (X, TikTok) vanish on one theme, so they
 *   are drawn in the site's grey, and a second one in a deeper grey. The
 *   leftover band is a pale tint of the same grey, so it stays distinct.
 * - Brands that look alike (Google, LinkedIn and Facebook are all blue) would
 *   make two bands one. The busier platform keeps its true colour; the quieter
 *   one tries its brand family's other colours first (Google Maps takes the
 *   Google green), then steps lighter or darker until it clears the floor.
 * - Platforms with no brand (web scraping, email tools) keep the hue their
 *   pictogram is drawn in, so the chart and the mark agree.
 *
 * Two colours collide when they are under 15 apart in OKLab x100: the
 * normal-vision floor of the dataviz skill's categorical palette check.
 */

import { brandHex, isMonoBrand } from '../../BrandIcon';
import { GOOGLE_G } from '../../brandMarks';
import { DIY_HUE } from '../../DiyMark';
import { platformMeta } from '../../../lib/catalogPlatform';
import { deltaE, hexToLab, labToHex } from '../../../lib/oklab';
import { isMonoHex } from '../../../lib/css';

const FLOOR = 15;

/** Google's own four colours, the alternates every Google property may take. */
const GOOGLE_ALTERNATES = GOOGLE_G.map((part) => part.fill.slice(1).toUpperCase());

/** Greys for monochrome brands, busiest first. Each leans further toward the
 *  theme's text colour, so two monochrome bands never share a grey and none of
 *  them is the leftover band's pale tint. */
const MONO_STEPS = [
  'var(--c-slate)',
  'color-mix(in oklab, var(--c-slate) 50%, var(--text))',
  'color-mix(in oklab, var(--c-slate) 20%, var(--text))',
];

/** Site hues for an unbranded platform whose pictogram hue is already taken. */
const SITE_HUES = ['var(--c-teal)', 'var(--c-amber)', 'var(--c-violet)', 'var(--c-green)'];

const isGoogleFamily = (cardId: string): boolean =>
  cardId === 'google' || cardId === 'maps' || cardId.startsWith('google-');

const clears = (hex: string, taken: string[]): boolean =>
  taken.every((other) => deltaE(hex, other) >= FLOOR);

/** The nearest lightness of `hex` that clears every taken colour and still
 *  reads on both themes, searched outward in 0.01 steps of OKLab L. */
const stepped = (hex: string, taken: string[]): string => {
  const [L, a, b] = hexToLab(hex);
  for (let d = 0.01; d <= 1; d += 0.01) {
    for (const next of [L - d, L + d]) {
      if (next <= 0 || next >= 1) continue;
      const candidate = labToHex([next, a, b]);
      if (!isMonoHex(candidate) && clears(candidate, taken)) return candidate;
    }
  }
  return hex;
};

/** The CSS colour for each band id, given in rank order. */
export const brandHues = (ids: string[]): Record<string, string> => {
  const hues: Record<string, string> = {};
  const taken: string[] = [];
  const used = new Set<string>();
  let monos = 0;

  for (const id of ids) {
    const cardId = platformMeta(id).id;
    const hex = brandHex(cardId)?.toUpperCase();
    if (!hex) {
      const own = DIY_HUE[cardId];
      const hue = own && !used.has(own) ? own : SITE_HUES.find((h) => !used.has(h));
      hues[id] = hue ?? 'var(--c-slate)';
      used.add(hues[id]);
      continue;
    }
    if (!isGoogleFamily(cardId) && isMonoBrand(cardId)) {
      hues[id] = MONO_STEPS[Math.min(monos++, MONO_STEPS.length - 1)];
      continue;
    }
    const options = isGoogleFamily(cardId) ? [hex, ...GOOGLE_ALTERNATES] : [hex];
    const clear = options.find((option) => clears(option, taken));
    if (clear) {
      taken.push(clear);
      hues[id] = `#${clear}`;
    } else {
      const next = stepped(hex, taken);
      taken.push(next);
      hues[id] = `#${next}`;
    }
  }
  return hues;
};
